import { get, writable } from 'svelte/store';

export const selfAssessmentTargetScore = 1.00;

export class SelfAssessmentStore {

    #activityModel;
    #store;

    constructor(activityModel) {
        this.#activityModel = activityModel;
        this.#store = writable(activityModel.get('self_assessment'));
    }

    subscribe(...args) {
        return this.#store.subscribe(...args);
    }

    update(...args) {
        return this.#store.update(...args);
    }

    changeTaskGroupsOrder(learningGoalId, taskGroupOrder) {

        this.update(state => {

            const index = _.findIndex(state.learning_goals, { id: learningGoalId });
            const learningGoal = state.learning_goals[index];

            const taskGroups = learningGoal.task_groups;
            const taskGroupMap = new Map(taskGroups.map(item => [item.id, item]));

            const newTaskGroups = taskGroupOrder.map(
                (id, sequence) => {
                    const taskGroup = taskGroupMap.get(parseInt(id));
                    taskGroup.sequence = sequence;
                    return taskGroup;
                }
            );

            jQuery.ajax({
                type: 'POST',
                url: `/self_assessment/learning_goals/update_taskgroup/${learningGoalId}`,
                data: {
                    task_groups: newTaskGroups.map(
                        ({ id, is_resit }) => { return { id, is_resit } }
                    )
                }
            });

            state.learning_goals[index].task_groups = newTaskGroups;

            return state;
        });
    }

    moveLearningGoal(id, direction) {

        this.update(state => {
            const index = _.findIndex(state.learning_goals, { id });

            if (index === -1) {
                return;
            }

            const newIndex = index + direction;

            // Check for out of bounds
            if (newIndex < 0 || newIndex >= state.learning_goals.length) {
                return;
            }

            // Swap values
            [
                state.learning_goals[index],
                state.learning_goals[newIndex]
            ] = [
                state.learning_goals[newIndex],
                state.learning_goals[index]
            ];

            jQuery.ajax({
                type: 'POST',
                url: `/self_assessment/update_sequence/${state.id}`,
                data: {
                    learning_goal_ids: _.pluck(state.learning_goals, 'id')
                }
            });

            return state;
        });
    }

    addLearningGoal(id, name) {
        this.update(state => {
            state.learning_goals.push({
                id,
                name,
                task_groups: []
            })

            return state;
        });
    }

    getTaskGroupModel(id) {
        return this.#activityModel.task_groups.get(id);
    }

    removeTaskGroup(learningGoalId, taskGroupId) {
        this.update(state => {

            const index = _.findIndex(state.learning_goals, { id: learningGoalId });

            const newTaskGroups = _.reject(
                state.learning_goals[index].task_groups,
                item => item.id === taskGroupId
            );

            this.#activityModel.task_groups.get(taskGroupId).destroy();

            newTaskGroups.forEach((taskGroup, sequence) => {
                taskGroup.sequence = sequence
            });

            state.learning_goals[index].task_groups = newTaskGroups

            return state;
        });
    }

    addTaskGroup(learningGoalId) {

        const activityModel = this.#activityModel;

        activityModel.task_groups.create(
            {
                sequence: activityModel.task_groups.length,
                parent_id: activityModel.id
            },
            {
                // wait so that adding task group goes OK
                wait: true,
                success: (newTaskGroupModel) => {

                    newTaskGroupModel.setIndex()
                    newTaskGroupModel.activityModel = activityModel;

                    jQuery.ajax({
                        type: 'POST',
                        url: `/self_assessment/learning_goals/add_taskgroup/${learningGoalId}`,
                        data: {
                            task_group_id: newTaskGroupModel.id
                        }
                    });

                    Backbone.history.navigate(
                        'task_groups/author/' + activityModel.id + '/' + newTaskGroupModel.id,
                        {trigger: true}
                    );
                }
            }
        )
    }

    markTaskGroupsAsResit(learningGoalId, resitTaskGroupIds) {

        this.update(state => {

            const learningGoal = _.findWhere(state.learning_goals, { id: learningGoalId })

            for (const taskGroup of learningGoal.task_groups) {
                taskGroup.is_resit = resitTaskGroupIds.includes(taskGroup.id);
            }

            jQuery.ajax({
                type: 'POST',
                url: `/self_assessment/learning_goals/update_taskgroup/${learningGoalId}`,
                data: {
                    // Filter out (for the backend) unwanted properties
                    task_groups: learningGoal.task_groups.map(
                        ({ id, is_resit }) => {
                            return {
                                id,
                                is_resit
                            }
                        }
                    )
                }
            });

            return state;
        });
    }

    updateLearningGoalName(id, name) {
        this.update(state => {
            const index = state.learning_goals.findIndex(g => g.id === id);

            const learning_goals = [...state.learning_goals];
            learning_goals[index] = {
                ...learning_goals[index],
                name
            };

            return { ...state, learning_goals };
        });
    }

    removeLearningGoal(id) {
        this.update(state => {
            const index = _.findIndex(state.learning_goals, { id });
            state.learning_goals.splice(index, 1);
            return state;
        })
    }

    isLearningGoalAchieved(learningGoal, studentId = false)
    {
        const activityModel = this.#activityModel;

        const mainTaskGroupIds = []
        const resitTaskGroupIds = []

        for (const taskGroup of learningGoal.task_groups) {
            if (taskGroup.is_resit === true) {
                resitTaskGroupIds.push(taskGroup.id)
            } else {
                mainTaskGroupIds.push(taskGroup.id)
            }
        }

        let taskIds = []

        for (const taskGroupId of mainTaskGroupIds) {
            const taskGroup = activityModel.task_groups.get(taskGroupId)
            taskIds = taskIds.concat(taskGroup.tasks.pluck('id'))
        }

        // The target score is 100% of all the main tasks for the learning goal
        const targetScore = taskIds.length * selfAssessmentTargetScore

        for (const taskGroupId of resitTaskGroupIds) {
            const taskGroup = activityModel.task_groups.get(taskGroupId)
            taskIds = taskIds.concat(taskGroup.tasks.pluck('id'))
        }

        // The achieved score is the score of the student on all tasks for the learning goal
        let achievedScore = 0
        for (const response of activityModel.responses) {
            if (response.get('score') >= 0
                && taskIds.includes(response.get('task_id'))
                && (studentId === false || response.get('user_id') === studentId)
            ) {
                achievedScore += response.get('score')
            }
        }

        return achievedScore >= targetScore
    }

    getAchievementStatusPerLearningGoalForStudent({ studentId }) {
        const state = get(this.#store);

        const progressMap = new Map();

        state.learning_goals.forEach(learningGoal => {
            progressMap.set(learningGoal.id, this.isLearningGoalAchieved(learningGoal, studentId));
        });

        return progressMap;
    }

    canBeEdited() {

        const state = get(this.#store);

        if (state.user_id === Backbone.Model.user.id) {
            return true
        }

        const groupModel = this.#activityModel.getGroupModel()

        return (groupModel
            && groupModel.get('collaboration_enabled')
            && groupModel.get('teachers').has(state.user_id)
        )
    }
}
